import React, { useState, useEffect } from 'react';
import { StyleSheet, css } from 'aphrodite';
import {
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  CircularProgress,
  IconButton,
  Chip
} from '@mui/material';
import {
  Notifications,
  NotificationsActive,
  MedicalServices,
  CalendarMonth,
  LocalPharmacy,
  Delete,
  CheckCircle
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Mock notifications data
const mockNotifications = [
  {
    id: 1,
    type: 'appointment',
    title: 'Upcoming Appointment',
    message:
			'You have an appointment with Dr. Sarah Johnson tomorrow at 10:00 AM.',
    date: '2023-09-15T10:00:00',
    read: false
  },
  {
    id: 2,
    type: 'medication',
    title: 'Medication Reminder',
    message: 'Time to take your Lisinopril medication.',
    date: '2023-09-14T08:00:00',
    read: true
  },
  {
    id: 3,
    type: 'treatment',
    title: 'Treatment Record Updated',
    message:
			'Dr. Michael Chen has updated your treatment record for Hypertension.',
    date: '2023-09-13T14:30:00',
    read: false
  },
  {
    id: 4,
    type: 'appointment',
    title: 'Appointment Confirmation',
    message:
			'Your appointment with Dr. Robert Williams has been confirmed for September 20th at 2:30 PM.',
    date: '2023-09-12T09:15:00',
    read: true
  },
  {
    id: 5,
    type: 'medication',
    title: 'Prescription Refill',
    message:
			'Your prescription for Metformin has been refilled and is ready for pickup.',
    date: '2023-09-10T16:45:00',
    read: false
  }
];

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Simulate API call to fetch notifications
    const fetchNotifications = async () => {
      try {
        // Replace with actual API call
        // const response = await fetch('/api/notifications');
        // const data = await response.json();

        // Using mock data for now
        setTimeout(() => {
          setNotifications(mockNotifications);
          setLoading(false);
        }, 1000);
      } catch (err) {
        setError('Failed to fetch notifications. Please try again later.');
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  const handleMarkAsRead = (id) => {
    setNotifications(
      notifications.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const handleDeleteNotification = (id) => {
    setNotifications(
      notifications.filter((notification) => notification.id !== id)
    );
  };

  const handleMarkAllAsRead = () => {
    setNotifications(
      notifications.map((notification) => ({ ...notification, read: true }))
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return (
        date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
				` at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      );
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment':
        return <CalendarMonth />;
      case 'medication':
        return <LocalPharmacy />;
      case 'treatment':
        return <MedicalServices />;
      default:
        return <Notifications />;
    }
  };

  const unreadCount = notifications.filter(
    (notification) => !notification.read
  ).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={css(styles.container)}
    >
      <div className={css(styles.headerSection)}>
        <div className={css(styles.titleContainer)}>
          <Typography variant='h4' className={css(styles.title)}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={`${unreadCount} unread`}
              color='primary'
              size='small'
              className={css(styles.unreadChip)}
            />
          )}
        </div>

        {notifications.length > 0 && (
          <Button
            variant='outlined'
            color='primary'
            onClick={handleMarkAllAsRead}
            startIcon={<CheckCircle />}
            disabled={unreadCount === 0}
          >
            Mark all as read
          </Button>
        )}
      </div>

      <Paper elevation={3} className={css(styles.contentPaper)}>
        {loading
          ? (
            <div className={css(styles.loadingContainer)}>
              <CircularProgress color='primary' />
              <Typography variant='body2'>Loading notifications...</Typography>
            </div>
            )
          : error
            ? (
              <div className={css(styles.errorContainer)}>
                <NotificationsActive className={css(styles.errorIcon)} />
                <Typography variant='body1' color='error'>
                  {error}
                </Typography>
              </div>
              )
            : notifications.length === 0
              ? (
                <div className={css(styles.emptyContainer)}>
                  <Notifications className={css(styles.emptyIcon)} />
                  <Typography variant='h6'>No notifications</Typography>
                  <Typography variant='body2' color='textSecondary'>
                    You don't have any notifications at the moment.
                  </Typography>
                </div>
                )
              : (
                <List className={css(styles.notificationsList)}>
                  {notifications.map((notification, index) => (
                    <React.Fragment key={notification.id}>
                      <ListItem
                        className={css(
									  styles.notificationItem,
									  !notification.read && styles.unreadNotification
                        )}
                        alignItems='flex-start'
                      >
                        <ListItemIcon className={css(styles.notificationIcon)}>
                          {getNotificationIcon(notification.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <div className={css(styles.notificationHeader)}>
                              <Typography
                                variant='subtitle1'
                                className={css(styles.notificationTitle)}
                              >
                                {notification.title}
                              </Typography>
                              <Typography variant='caption' color='textSecondary'>
                                {formatDate(notification.date)}
                              </Typography>
                            </div>
										}
                          secondary={
                            <Typography
                              variant='body2'
                              className={css(styles.notificationMessage)}
                            >
                              {notification.message}
                            </Typography>
										}
                        />
                        <div className={css(styles.notificationActions)}>
                          {!notification.read && (
                            <IconButton
                              size='small'
                              color='primary'
                              onClick={() => handleMarkAsRead(notification.id)}
                              title='Mark as read'
                            >
                              <CheckCircle fontSize='small' />
                            </IconButton>
                          )}
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() => handleDeleteNotification(notification.id)}
                            title='Delete notification'
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </div>
                      </ListItem>
                      {index < notifications.length - 1 && <Divider component='li' />}
                    </React.Fragment>
                  ))}
                </List>
                )}
      </Paper>
    </motion.div>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  headerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    '@media (max-width: 600px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '1rem'
    }
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  title: {
    color: '#2783F7',
    fontWeight: 'bold',
    fontSize: '1.8rem',
    '@media (max-width: 600px)': {
      fontSize: '1.5rem'
    }
  },
  unreadChip: {
    backgroundColor: '#2783F7',
    color: 'white'
  },
  contentPaper: {
    padding: '1.5rem',
    borderRadius: '8px',
    minHeight: '400px'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 0',
    gap: '1rem'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 0',
    gap: '1rem'
  },
  errorIcon: {
    fontSize: '3rem',
    color: '#f44336'
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem 0',
    gap: '0.5rem'
  },
  emptyIcon: {
    fontSize: '4rem',
    color: '#9e9e9e',
    marginBottom: '1rem'
  },
  notificationsList: {
    padding: 0
  },
  notificationItem: {
    padding: '1rem'
  },
  unreadNotification: {
    backgroundColor: '#e6f0fd'
  },
  notificationIcon: {
    color: '#2783F7',
    minWidth: '40px'
  },
  notificationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '@media (max-width: 600px)': {
      flexDirection: 'column',
      alignItems: 'flex-start'
    }
  },
  notificationTitle: {
    fontWeight: 'bold'
  },
  notificationMessage: {
    color: '#555',
    marginTop: '0.5rem'
  },
  notificationActions: {
    display: 'flex',
    gap: '0.25rem'
  }
});

export default NotificationsPage;
